"use client";

import { useMemo } from "react";
import { ThemeProvider, type Theme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { forestTheme } from "../theme";
import { getPreset } from "../theme/presets";
import { buildTheme } from "../theme/buildTheme";

export interface ForestProviderProps {
  children: React.ReactNode;
  theme?: Theme;
  preset?: string;
  variant?: string;
  disableCssBaseline?: boolean;
}

export function ForestProvider({
  children,
  theme,
  preset,
  variant,
  disableCssBaseline = false,
}: ForestProviderProps) {
  const resolvedTheme = useMemo(() => {
    // Explicit theme prop takes highest priority
    if (theme) return theme;

    // If no preset specified, use the default forest theme
    if (!preset) return forestTheme;

    // Resolve preset + variant
    const presetConfig = getPreset(preset);
    if (!presetConfig) {
      throw new Error(
        `[ForestProvider] Unknown preset "${preset}". ` +
        `Available presets: use getAvailablePresets() to see registered presets.`
      );
    }

    const variantName = variant ?? presetConfig.defaultVariant;
    const variantConfig = presetConfig.variants[variantName];
    if (!variantConfig) {
      throw new Error(
        `[ForestProvider] Unknown variant "${variantName}" for preset "${preset}". ` +
        `Available variants: ${Object.keys(presetConfig.variants).join(", ")}.`
      );
    }

    return buildTheme(variantConfig.tokens);
  }, [theme, preset, variant]);

  return (
    <ThemeProvider theme={resolvedTheme}>
      {!disableCssBaseline && <CssBaseline />}
      {children}
    </ThemeProvider>
  );
}
